import type { FandomCmp } from "common-types";
import type { OpenRtb2Object } from "../OpenRtbPlugin/openRtb";
import invariant from "tiny-invariant";

import type {
  ApsTag,
  A9CCPA,
  A9Bid,
  A9WrapperConfig,
  A9ConsentParams,
  ApstagConfigParams,
} from "./a9";
import { A9HemSyncer } from "./A9HemSyncer";
import type { AdNode } from "../../core/AdNode";
import type { AuctionData, Hooks } from "../../core/AdEnginePlugin";

export class A9Wrapper {
  private readonly apstagConfigParams: ApstagConfigParams;
  private readonly a9HemSyncer: A9HemSyncer;

  constructor(
    private consentData: FandomCmp,
    private readonly timeout: number,
    private readonly config: A9WrapperConfig,
    ortb2?: OpenRtb2Object,
  ) {
    this.apstagConfigParams = {
      pubID: "3115",
      adServer: "googletag",
      videoAdServer: "DFP",
      simplerGPT: true,
      deals: true,
      bidTimeout: this.timeout,
      signals: ortb2 ? { ortb2 } : {},
    };

    this.a9HemSyncer = new A9HemSyncer(this.config, this.consentData);

    this.init(true);
  }

  private configureCommand(command: string, args: object[]): void {
    (window.apstag as ApsTag)._Q.push([command, args]);
  }

  updateApstagOrtb2Signals(ortb2?: OpenRtb2Object): void {
    // If for whatever reason the ortb2 object is not provided, we do not update anything.
    if (ortb2) {
      this.apstagConfigParams.signals.ortb2 = ortb2;
      this.updateApstagConfig();
    }
  }

  private updateApstagConfig() {
    if (window.apstag) {
      const { gdprParam, uspParam } = this.getConsentsParams();
      window.apstag.init({
        ...this.apstagConfigParams,
        ...gdprParam,
        ...uspParam,
      });
    }
  }

  /**
   * Initializes the A9Wrapper by setting up the `apstag` object and configuring it with consent parameters.
   * Optionally updates the Amazon Publisher Audience configuration and cleans up A9 tokens if the variable is set in ICBM.
   *
   * @param {boolean} [firstCall] - Indicates whether this is the first initialization call.
   *                                If true, it updates the Publisher Audience configuration.
   */
  init(firstCall?: boolean) {
    window.apstag = window.apstag ?? {
      _Q: [],
      init: (...args: object[]) => {
        this.configureCommand("i", args);
      },
      fetchBids: (...args) => {
        this.configureCommand("f", args);
      },
    };

    const a9ConsentParams = this.getConsentsParams();
    this.updateApstagConfig();
    if (firstCall) {
      this.a9HemSyncer.updateApstagPublisherAudienceConfig(a9ConsentParams);
    }
  }

  private getConsentsParams(): A9ConsentParams {
    return {
      gdprParam:
        this.consentData.consentApi === "tcf"
          ? {
              gdpr: {
                cmpTimeout: this.timeout,
              },
            }
          : {
              gdpr: {
                enabled: false,
              },
            },
      uspParam: this.getUspParam(),
    };
  }

  handleConsentChange(consentData: FandomCmp): void {
    this.consentData = consentData;
    const a9ConsentParams = this.getConsentsParams();
    this.updateApstagConfig();
    this.a9HemSyncer.updateConsentData(consentData, a9ConsentParams);
  }

  private getUspParam(): Partial<A9CCPA> {
    if (
      this.consentData.consentApi === "usp" &&
      this.consentData.consentString
    ) {
      return {
        params: {
          us_privacy: this.consentData.consentString,
        },
      };
    }

    return {};
  }

  async requestBids(
    auctionData: AuctionData,
    onCreateBidderAdUnit: NonNullable<Hooks["onCreateBidderAdUnit"]>,
  ): Promise<A9Bid[]> {
    if (!this.consentData.allowed.ads || this.consentData.coppa) {
      return [];
    }

    // make sure we have at least the stub
    invariant(window.apstag, "window.apstag has not been initialized yet");

    const bidConfig = {
      slots: this.buildBidsConfig(auctionData.adNodes),
      timeout: this.timeout,
    };

    if (!bidConfig.slots.length) {
      // apstag.fetchBids will return an error if provided an empty slots array
      return [];
    }

    onCreateBidderAdUnit("a9", auctionData, bidConfig);

    return new Promise<A9Bid[]>((resolve) => {
      window.apstag?.fetchBids(bidConfig, (currentBids) => {
        window.apstag?.setDisplayBids?.();
        resolve(currentBids);
      });
    });
  }

  private buildBidsConfig(adNodes: AdNode[]): A9SlotDefinition[] {
    const bidsConfig: A9SlotDefinition[] = [];

    adNodes.forEach((adNode) => {
      const a9Config = adNode.getBidderConfig("a9");
      if (!a9Config) return;

      const slotName = adNode.getSlotName();
      const config: A9SlotDefinition = {
        slotName: adNode.getAdUnitPath() ?? slotName,
        slotID: slotName,
        ...a9Config,
      };

      bidsConfig.push(config);
    });

    return bidsConfig;
  }
}
